/* eslint-disable no-useless-escape */
import axios from 'axios';
import { captureException } from '@sentry/react';

import pushEvent, {
  EVENT_PROMO_VIEW_BANNERS,
  EVENT_PROMO_CLICK_BANNER,
  EVENT_PROMO_VIEW_SLIDER,
  EVENT_PROMO_MOVE_SLIDER,
  EVENT_BANNER_BLOCKS_BANNER_VIEW,
  EVENT_BANNER_BLOCKS_BANNER_CLICK,
  EVENT_PRODUCT_DAY_VIEW,
  EVENT_PRODUCT_DAY_CLICK,
  EVENT_PRODUCT_ADD_TO_CART,
} from 'pushEvent';
import {
  getBannerContainerList,
  getUserRegionTitle,
  getSharedZoneFederalRegionTitle,
} from 'store/SharedZone/selectors';
import { remainsNumToText } from 'pushEvent/helpers';
import { FOLLOW_LINK } from 'store/constants';
import { getCurrentCategoriesPathYM } from 'store/common/navigation/selectors';

import { SET_PRODUCT_SLIDER_LIST, SET_PRODUCT_SLIDER_POSITION, SET_BANNER_VIEW } from './constants';

const pushViewBanners = (banners, dispatch, index, isMobile) => {
  const viewBanners = banners || [];
  const mappedBanners = viewBanners.map((banner) => {
    let name = '';
    let creative = '';
    if (banner.markup && banner.markup.startsWith('<')) {
      [, name] = /data-banner_name=\"(.*?)\"/.exec(banner.markup);
      [, creative] = /src=\"(.*?)\"/.exec(banner.markup);
    } else if (banner.markup) {
      const bannerObj = JSON.parse(banner.markup);
      name = bannerObj.title || '';
      creative = isMobile ? bannerObj.imageMobile : bannerObj.imageDesktop;
    } else {
      name = banner.title;
      creative =
        isMobile ?
          banner.webpMobileBgImageUrl || banner.bgImageMobileUrl || ''
        : banner.webpBgImageUrl || banner.bgImageUrl || '';
    }
    /** regexp deletes http://, https://, // at the begining url */
    const re = new RegExp(/^http(s?)\:\/\/|\/\//i);
    return {
      name,
      creative: creative.replace(re, ''),
      position: `${index + 1}`,
    };
  });

  if (viewBanners[index] && !viewBanners[index].isView) {
    // isView - это флаг, который показывает
    // был ли показан баннер для аналитики
    // чтобы показать баннер только один раз
    pushEvent(EVENT_PROMO_VIEW_BANNERS, [mappedBanners[index]]);
    dispatch({
      type: SET_BANNER_VIEW,
      payload: index,
    });
  }
};

export const onMountBanners = (isMobile) => (dispatch, getState) => {
  const state = getState();
  const banners = getBannerContainerList(state);
  pushViewBanners(banners, dispatch, 0, isMobile);
};

export const onSwitchBanners = (eq, isMobile) => (dispatch, getState) => {
  const state = getState();
  const index = eq || 0;
  const banners = getBannerContainerList(state);
  pushViewBanners(banners, dispatch, index, isMobile);
};

export const onClickBanner = (banner, index, isMobile) => () => {
  let name = '';
  let creative = '';
  let banners = [];
  if (banner.markup && banner.markup.startsWith('<')) {
    [, name] = /data-banner_name=\"(.*?)\"/.exec(banner.markup);
    [, creative] = /src=\"(.*?)\"/.exec(banner.markup);
  } else if (banner.markup) {
    const bannerObj = JSON.parse(banner.markup);
    name = bannerObj.title || '';
    creative = isMobile ? bannerObj.imageMobile : bannerObj.imageDesktop;
  } else {
    name = banner.title;
    creative =
      isMobile ?
        banner.webpMobileBgImageUrl || banner.bgImageMobileUrl || ''
      : banner.webpBgImageUrl || banner.bgImageUrl || '';
  }
  banners = [
    {
      name,
      creative: creative.replace(/^http(s?)\:\/\/|\/\//i, ''),
      position: `${index + 1}`,
    },
  ];
  pushEvent(EVENT_PROMO_CLICK_BANNER, banners);
};

export const onViewSlider = (list, size, listHeading, blockId) => (dispatch, getState) => {
  const state = getState();
  const userRegionTitle = getUserRegionTitle(state);
  const federalRegionName = getSharedZoneFederalRegionTitle(state);
  const slides = (list || []).slice(0, size);
  const category = getCurrentCategoriesPathYM(state);
  const data = {
    slides,
    listHeading,
    userRegionTitle,
    federalRegionName,
    category,
  };
  pushEvent(EVENT_PROMO_VIEW_SLIDER, data);
  dispatch({
    type: SET_PRODUCT_SLIDER_POSITION,
    payload: {
      blockId,
      start: 0,
    },
  });
};

export const onMoveSlider = (list, size, start, blockId, position) => (dispatch, getState) => {
  const state = getState();
  const userRegionTitle = getUserRegionTitle(state);
  const federalRegionName = getSharedZoneFederalRegionTitle(state);
  const slides = (list || []).slice(start, start + size);
  const newSlides = [];
  if (position < start) {
    newSlides.push(slides[size - 1]);
  } else {
    newSlides.push(slides[0]);
  }
  const category = getCurrentCategoriesPathYM(state);
  const data = {
    slides: newSlides,
    userRegionTitle,
    federalRegionName,
    category,
  };
  pushEvent(EVENT_PROMO_MOVE_SLIDER, data);
  dispatch({
    type: SET_PRODUCT_SLIDER_POSITION,
    payload: {
      blockId,
      start,
    },
  });
};

export const getSliderAsyncData = (blockId, requestUrl, requestParams) => (dispatch) => {
  axios({
    method: 'get',
    url: requestUrl,
    responseType: 'json',
    params: requestParams,
  })
    .then((response) => {
      const data = response.data || {};
      if (data.isSucceeded) {
        const list = (data.data || {}).products || [];
        dispatch({
          type: SET_PRODUCT_SLIDER_LIST,
          payload: {
            blockId,
            list,
          },
        });
      } else {
        // TODO: parse errors
        console.info(data);
      }
    })
    .catch(captureException);
};

export const onViewBannerBlocksBanner =
  ({ name, creative, position }) =>
  () => {
    pushEvent(EVENT_BANNER_BLOCKS_BANNER_VIEW, {
      name,
      creative,
      position,
    });
  };

export const onClickBannerBlocksBanner =
  ({ name, creative, position }) =>
  () => {
    pushEvent(EVENT_BANNER_BLOCKS_BANNER_CLICK, {
      name,
      creative,
      position,
    });
  };

export const onViewProductDay = (product) => (dispatch, getState) => {
  const state = getState();
  const { productId, brand, label, categoryId, title, price, rate, discount, remain } = product;

  const data = {
    regionName: getUserRegionTitle(state),
    federalRegionName: getSharedZoneFederalRegionTitle(state),
    brand,
    category: categoryId,
    id: productId,
    list: label,
    name: title,
    price: (price || {}).value,
    metric15: rate || '',
    metric14: discount || '',
    dimension15: null,
    dimension16: remainsNumToText(remain),
    dimension17: null,
  };
  pushEvent(EVENT_PRODUCT_DAY_VIEW, data);
};

export const onСlickProductDay = (product) => (dispatch, getState) => {
  const state = getState();
  const { productId, brand, label, categoryId, title, price, rate, discount, remain } = product;
  const data = {
    regionName: getUserRegionTitle(state),
    federalRegionName: getSharedZoneFederalRegionTitle(state),
    actionField: {
      list: label,
    },
    brand,
    category: categoryId,
    id: productId,
    name: title,
    price: (price || {}).value,
    metric15: rate || '',
    metric14: discount || '',
    dimension15: null,
    dimension16: remainsNumToText(remain),
    dimension17: null,
  };
  pushEvent(EVENT_PRODUCT_DAY_CLICK, data);

  dispatch({
    type: FOLLOW_LINK,
    payload: window.location.pathname,
  });
};

export const addToCartProductDay = (product) => (dispatch, getState) => {
  const state = getState();
  const { productId, brand, label, categoryId, title, price, rate, discount, remain } = product;
  const data = {
    regionName: getUserRegionTitle(state),
    federalRegionName: getSharedZoneFederalRegionTitle(state),
    actionField: {
      list: label,
    },
    brand,
    category: categoryId,
    id: productId,
    name: title,
    price: (price || {}).value,
    metric15: rate || '',
    metric14: discount || '',
    dimension15: null,
    dimension16: remainsNumToText(remain),
    dimension17: null,
    quantity: 1,
  };
  pushEvent(EVENT_PRODUCT_ADD_TO_CART, data);
};
